// ============================================================
// PlateThumb - any body small and still (plan body-map P4, P8): the gallery card, the targeting console, the actor's
// plain status. A person or a vehicle is its line art in one colour (health on the card, steel in the console, the
// actor's own); in the console the location being aimed at is lit in the actor's colour and the others fade, and each
// location gets the caller's targeting node at its anchor. The amorphous mass is its settled goo. No callouts, marks
// or wounds: at this size they would be noise.
// ============================================================

import type { CSSProperties, ReactNode } from 'react';
import type { PathGroup, PlateFigureId } from '../../engine/plate/figureArt';
import type { PlateFigure } from '../../engine/plate/figureFor';
import type { VehicleKind } from '../../engine/vehicleKind';
import styles from '../../styles/BodyPlate.module.css';
import { GOO_BOX, GooMass } from './PlateGoo';
import {
  ArtDefs,
  LocationClips,
  regionsOf,
  ScanUnavailable,
  usePlateUid,
  vehicleRegions,
  type Regions,
} from './plateParts';
import { usePlateArt, useVehicleArt } from './usePlateArt';
import { ObjectThumb } from './PlateObject';

/** The figure's own frame (the art's 2123 x 3000), a little headroom so the head's node is not cut. */
const LINES_VIEWBOX = '0 -60 2123 3120';
/** The settled goo's box, with a margin. */
const GOO_VIEWBOX = [GOO_BOX.x0 - 30, GOO_BOX.y0 - 30, GOO_BOX.x1 - GOO_BOX.x0 + 60, GOO_BOX.y1 - GOO_BOX.y0 + 60].join(
  ' ',
);

interface Props {
  body: PlateFigure;
  /** The lines' colour (any CSS colour). */
  colour: string;
  ariaLabel: string;
  className?: string;
  /** A location id to light in the actor's colour (the others fade); 'auto' or absent lights none. */
  selected?: string;
  opacity?: number;
  /** Something to draw at each location's anchor (the console's targeting node). */
  zone?: (id: string, at: readonly [number, number]) => ReactNode;
  /** The places crippled or destroyed: a red dot at each one's anchor (round 3's ruling; plan design-gaps, P8). */
  hurt?: readonly string[];
}

export default function PlateThumb({ body, ...rest }: Props) {
  if (body.kind === 'vehicle') return <VehicleThumb kind={body.vehicle} {...rest} />;
  if (body.kind === 'goo') return <GooThumb {...rest} />;
  // An Object (2026-10-26): its diamond, in its livery - it has no traced art and no locations to light.
  if (body.kind === 'object')
    return <ObjectThumb ariaLabel={rest.ariaLabel} className={rest.className} opacity={rest.opacity} />;
  return <PersonThumb figure={body.figure} {...rest} />;
}

type ThumbProps = Omit<Props, 'body'>;

/** What a line thumbnail draws, a person's art or a vehicle's: its lines, each location's anchor, its line weight. */
interface ThumbArt {
  id: string;
  art: PathGroup;
  anchors: Record<string, readonly [number, number]>;
  lineWeight: number;
}

/** A drawing: the art and its locations' clips, or null while it loads. */
type Drawing = { art: ThumbArt; regions: Regions } | null;

function PersonThumb({ figure, ...rest }: ThumbProps & { figure: PlateFigureId }) {
  const { art, failed } = usePlateArt(figure);
  if (failed) return <ScanUnavailable ariaLabel={rest.ariaLabel} className={rest.className} />;
  return <LinesThumb drawing={art && { art, regions: regionsOf(art) }} {...rest} />;
}

function VehicleThumb({ kind, ...rest }: ThumbProps & { kind: VehicleKind }) {
  const { art, failed } = useVehicleArt(kind);
  if (failed) return <ScanUnavailable ariaLabel={rest.ariaLabel} className={rest.className} />;
  return <LinesThumb drawing={art && { art, regions: vehicleRegions(art) }} {...rest} />;
}

function LinesThumb({
  drawing,
  colour,
  ariaLabel,
  className,
  selected,
  opacity = 0.85,
  zone,
  hurt,
}: ThumbProps & { drawing: Drawing }) {
  const u = usePlateUid('thumb');
  const style = { color: colour, opacity, '--lw': drawing?.art.lineWeight ?? 1 } as CSSProperties;
  return (
    <svg
      viewBox={LINES_VIEWBOX}
      className={className}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label={ariaLabel}
      data-figure={drawing?.art.id}
      style={style}
    >
      {drawing && <ThumbLines u={u} drawing={drawing} selected={selected} />}
      {drawing && zone && Object.keys(drawing.regions.clip).map((id) => zone(id, drawing.art.anchors[id]))}
      {drawing &&
        hurt?.map((id) => {
          const at = drawing.art.anchors[id];
          return at && <circle key={id} className={styles.thumbHurt} cx={at[0]} cy={at[1]} r={70} data-hurt={id} />;
        })}
    </svg>
  );
}

/** The lines: one pass in the colour, or - with a location selected - each location under its clip, lit or faded. */
function ThumbLines({ u, drawing, selected }: { u: string; drawing: NonNullable<Drawing>; selected?: string }) {
  if (!selected || selected === 'auto') {
    return (
      <>
        <ArtDefs u={u} art={drawing.art} />
        <use className={styles.thumbLines} href={`#${u}-art`} fill="currentColor" />
      </>
    );
  }
  const ids = Object.keys(drawing.regions.clip);
  return (
    <>
      <ArtDefs u={u} art={drawing.art}>
        <LocationClips u={u} regions={drawing.regions} />
      </ArtDefs>
      {ids.map((id) => (
        <g key={id} clipPath={`url(#${u}-c-${id})`} className={id === selected ? styles.thumbLit : styles.thumbFaded}>
          <use className={styles.thumbLines} href={`#${u}-art`} fill="currentColor" />
        </g>
      ))}
    </>
  );
}

/** The goo, small: its settled mass in the colour, without the volume's scanlines (they would be noise). */
function GooThumb({ colour, ariaLabel, className, opacity = 0.85 }: ThumbProps) {
  const u = usePlateUid('thumbgoo');
  return (
    <svg
      viewBox={GOO_VIEWBOX}
      className={className}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label={ariaLabel}
      data-figure="goo"
      style={{ '--line': colour, opacity } as CSSProperties}
    >
      <GooMass u={u} volume={false} />
    </svg>
  );
}
